import { useState, useEffect, useCallback, useRef } from "react";
import { MathJaxContext } from "better-react-mathjax";
import {
  ChevronLeft,
  ChevronRight,
  ListVideo,
  X,
  Video,
  FileText,
  Image as LucideImage,
  HelpCircle,
  Globe,
} from "lucide-react";
import SmartPlayer from "./SmartPlayer";
import dummyData from "./dummyData";
import { useLocation, useNavigate } from "react-router-dom";
//import BackButton from "../../components/common/BackButton";
import { addContentUsageLog } from "../../lib/LiveclassContent";

import { ArrowLeft } from "lucide-react";

const mathJaxConfig = {
  loader: { load: ["[tex]/ams"] },
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    packages: { "[+]": ["ams"] },
  },
};


function PlayerPage() {
  const [watermark, setWatermark] = useState("");
  const [contents, setContents] = useState([]);
  const [index, setIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  //const [baseUrl, setBaseUrl] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  //const passedUrl = location.state?.url;
  const stateContents =
    location.state?.contents;
  const mcqTestData =
    location.state?.testData;

  const playerType =
    location.state?.type;
  const detectType = (url) => {
    const lower = url.toLowerCase();



    if (lower.endsWith(".mp4") || lower.includes(".m3u8")) return "video";
    if (lower.endsWith(".mp3")) return "audio";
    if (lower.endsWith(".pdf")) return "pdf";
    if (lower.endsWith(".jpg") || lower.endsWith(".png")) return "image";
    if (lower.endsWith(".html")) return "html";
    if (lower.endsWith(".doc") || lower.endsWith(".docx")) return "doc";

    return "unknown";
  };
  useEffect(() => {


    // ======================================
    // MCQ DATA FROM NAVIGATION
    // ======================================

    if (
      playerType === "mcq" &&
      mcqTestData
    ) {

      console.log(
        "MCQ TEST DATA:",
        mcqTestData
      );

      // convert API response into player content

      const mcqContent = {
        id: 1,
        type: "mcq",

        testId:
          mcqTestData?.questions?.[0]
            ?.testid,
        title: "MCQ Test",

        questions: mcqTestData.questions.map(
          (question) => {

            const answers =
              mcqTestData.answers
                .filter(
                  (ans) =>
                    ans.qid === question.qid
                )
                .sort(
                  (a, b) => a.ansno - b.ansno
                );

            return {
              qid: question.qid,

              qno:
                question.qsno ||
                question.qno,

              question:
                question.qtext,

              options: answers.map(
                (ans) => ({
                  text:
                    ans.anstext,

                  isCorrect:
                    ans.iscorrect,

                  ansno:
                    ans.ansno,
                })
              ),
            };
          }
        ),
      };

      setContents([mcqContent]);

      return;
    }

    // ======================================
    // NORMAL CONTENTS
    // ======================================

    if (
      stateContents &&
      Array.isArray(stateContents)
    ) {

      setContents(stateContents);

      return;
    }


    const urlParams = new URLSearchParams(window.location.search);

    const stateUrl = location.state?.url;   // ✅ FROM NAVIGATION
    const jsonUrl = urlParams.get("jsonUrl");
    const dataParam = urlParams.get("data");

    // ✅ PRIORITY 1: navigation state
    if (stateUrl) {
      console.log("Received URL from state:", stateUrl);

      setContents([
        {
          id: 1,
          type: detectType(stateUrl),
          url: stateUrl,
          title: "Content"
        }
      ]);

      return;
    }

    // ✅ PRIORITY 2: JSON URL
    if (jsonUrl) {
      fetch(jsonUrl)
        .then((res) => res.json())
        .then((data) => setContents(data.contents || []))
        .catch(console.error);
      return;
    }

    // ✅ PRIORITY 3: encoded data
    if (dataParam) {
      try {
        const decoded = decodeURIComponent(decodeURIComponent(dataParam));
        const parsed = JSON.parse(decoded);
        setContents(parsed.contents || []);
        return;
      } catch (err) {
        console.error(err);
      }
    }

    // ❌ LAST fallback
    setContents(dummyData.contents);

  }, [location.state]);

  const usageLogId = location.state?.usageLogId;
  //===============log maintain cntent usages ========
  const updateUsageLog = async () => {

    if (!usageLogId) return;

    try {

      await addContentUsageLog({
        ID: String(usageLogId),
        endTime: new Date(),
      });

      console.log("Usage log updated");

    } catch (err) {

      console.error("Update log failed", err);

    }
  };

  useEffect(() => {

    const handleUnload = () => {

      if (!usageLogId) return;

      navigator.sendBeacon(
        "http://localhost:5050/log/AddContentUsageLog",
        new Blob(
          [
            JSON.stringify({
              ID: String(usageLogId),
              endTime: new Date(),
            }),
          ],
          { type: "application/json" }
        )
      );

    };

    window.addEventListener(
      "beforeunload",
      handleUnload
    );

    return () => {

      window.removeEventListener(
        "beforeunload",
        handleUnload
      );

    };

  }, [usageLogId]);


  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const next = useCallback(() => {
    if (index < contents.length - 1) setIndex(index + 1);
  }, [index, contents.length]);

  const prev = useCallback(() => {
    if (index > 0) setIndex(index - 1);
  }, [index]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        index === contents.length - 1 ? prev() : next();
      } else if (e.code === "ArrowRight") next();
      else if (e.code === "ArrowLeft") prev();
      else if (e.code === "KeyS") toggleSidebar();
    },
    [next, prev, index, contents.length],
  );
  const current = contents[index];

  // useEffect(() => {
  //   if (!current) return;

  //   if (current.type === "mcq" && current.url && !current.questions) {
  //     fetch(current.url)
  //       .then((res) => res.json())
  //       .then((data) => {
  //         setContents((prev) => {
  //           const newContents = [...prev];

  //           newContents[index] = {
  //             ...newContents[index],
  //             questions: data.questions || [],
  //             totalQuestions: data.totalQuestions || 0,
  //             loaded: true,
  //           };

  //           return newContents;
  //         });
  //       })
  //       .catch((err) => console.error("MCQ load error:", err));
  //   }
  // }, [current, index]);
  if (!contents.length) {
    console.log(contents, "No contents to display");
    return (
      <div className="flex items-center justify-center min-h-dvh bg-gradient-to-br from-gray-900 to-black text-white text-lg sm:text-2xl px-4 text-center">
        Loading Contents...
      </div>
    );
  }




  return (
    // <div className="flex flex-col h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black text-white">
    <div className="flex flex-col h-full min-h-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-black text-white overflow-hidden">

      {/* <div className="p-2 border-b border-gray-800">

        <button
          onClick={() => {

            // if return state exists
            if (location.state?.returnState) {

              navigate("/recorded-session", {
                state: {
                  returnState: location.state.returnState,
                },
              });

            } else {

              navigate(-1);

            }

          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition"
        >
          <ArrowLeft size={18} />
          Back
        </button>

      </div> */}

      {/* PLAYER AREA */}
      {/* <div className="flex-1 relative bg-black overflow-auto"> */}
      <div className="flex-1 relative bg-black overflow-hidden w-full min-h-0">

        {/* Watermark */}
        {watermark && (
          <div className="absolute top-10 left-0 z-50 pointer-events-none animate-watermark text-red-500 text-xl font-bold whitespace-nowrap">
            {watermark}
          </div>
        )}


        {/* <div className="w-full h-full"> */}
        <div className="w-full h-full flex items-center justify-center p-2 sm:p-3 md:p-0">

          <MathJaxContext config={mathJaxConfig} hideUntilTypeset="first">
            <SmartPlayer key={index} content={current} onNext={next} />
          </MathJaxContext>
        </div>
      </div>


      {/* <div className="bg-gray-900 border-t border-gray-800 px-3 py-1 flex items-center gap-4">
      
      
      <select
        value={String(index)}
        onChange={(e) => {
          const newIndex = parseInt(e.target.value, 10);
          setIndex(newIndex);
        }}
        className="bg-black text-white border border-gray-700 rounded px-2 py-1"
      >
        {contents.map((item, i) => (
          <option key={item.id} value={String(i)}>
            {i + 1}
          </option>
        ))}
      </select>

    

      
      <div className="flex gap-2">
        <button
          onClick={prev}
          className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600"
        >
          Prev
        </button>
        <button
          onClick={next}
          className="px-3 py-1 bg-indigo-600 rounded hover:bg-indigo-500"
        >
          Next
        </button>
      </div>
    </div> */}
      {/* <div className="bg-gray-900 border-t border-gray-800 px-3 py-1 flex items-center gap-4"> */}
      <div className="shrink-0 bg-gray-900 border-t border-gray-800 px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 z-50">

        {/* Drop-up */}
        <div className="relative self-start sm:self-auto">
          {contents.length > 1 && (
            <button
              onClick={() => setOpen((prev) => !prev)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1 min-w-[60px]"
            >
              {index + 1}
            </button>
          )}

          {open && (
            <div className="absolute bottom-full mb-1 w-10 max-h-60 overflow-y-auto bg-gray-900 border border-gray-700 rounded shadow-lg z-50">
              {contents.map((item, i) => (
                <div
                  key={item.id}
                  onClick={() => {
                    setIndex(i);
                    setOpen(false);
                  }}
                  className={`px-2 py-1 cursor-pointer text-center hover:bg-indigo-600 ${i === index ? "bg-indigo-500" : ""
                    }`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        {/* <div className="flex gap-2"> */}
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {/* <button
            onClick={prev}
            className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600"
          >
            Prev
          </button>
          <button
            onClick={next}
            className="px-3 py-1 bg-indigo-600 rounded hover:bg-indigo-500"
          >
            Next
          </button>

           <button
          onClick={() => {

            // if return state exists
            if (location.state?.returnState) {

              navigate("/recorded-session", {
                state: {
                  returnState: location.state.returnState,
                },
              });

            } else {

              navigate(-1);

            }

          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition"
        >
          <ArrowLeft size={18} />
          Back
        </button> */}

          <button
            onClick={prev}
            className="flex-1 sm:flex-none px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition min-w-[84px]"
          >
            Prev
          </button>

          <button
            onClick={next}
            className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-500 transition min-w-[84px]"
          >
            Next
          </button>

          <button

            onClick={async () => {

              await updateUsageLog();

              if (location.state?.returnState) {

                navigate("/recorded-session", {
                  state: {
                    returnState: location.state.returnState,
                  },
                });

              } else {

                navigate(-1);

              }
            }}
            //  onClick={() => {
            //   if (location.state?.returnState) {
            //     navigate("/recorded-session", {
            //       state: {
            //         returnState: location.state.returnState,
            //       },
            //     });
            //   } else {
            //     navigate(-1);
            //   }
            // }}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition flex-1 sm:flex-none min-w-[84px]"
          >
            <ArrowLeft size={18} />
            Back
          </button>

        </div>
      </div>
    </div>

  );
}

export default PlayerPage;
